import React, { useState, useCallback, useEffect } from 'react';
import {
  Card,
  Group,
  Stack,
  Text,
  Button,
  Grid,
  Badge,
  Alert,
  LoadingOverlay,
  Box
} from '@mantine/core';

// Add a typesafe declaration for our temp storage
declare global {
  interface Window {
    _latestClientSecret?: string;
  }
}
import { 
  IconCheck, 
  IconAlertCircle, 
  IconInfoCircle, 
  IconCreditCard
} from '@tabler/icons-react';
import { Check } from 'lucide-react';
import { useSubscriptionFeatures } from '../hooks/useSubscriptionFeatures';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useMedplum } from '@medplum/react';
import { showNotification } from '@mantine/notifications';
import { trackStartTrial, trackSubscribe } from './FacebookPixel';

// Initialize Stripe with your publishable key
// Use window as a fallback for development
const stripeKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 
  (window as any).VITE_STRIPE_PUBLISHABLE_KEY || 
 // 'pk_test_placeholder';
 'pk_test_jIqc6QAfldl6cJQ2zemJUGlv';
const stripePromise = loadStripe(stripeKey);

// Pricing data
// Special pricing (33% off regular prices)
const monthlyPrice = {
  basic: 9.99, // Text Companion (was 14.99)
  premium: 19.99 // Voice Connect (was 29.99)
};

const annualPrice = {
  basic: 7.99, // Text Companion: $95.88/year ÷ 12 = ~$7.99/month (was 11.99)
  premium: 15.99 // Voice Connect: $191.88/year ÷ 12 = ~$15.99/month (was 23.99)
};

const basicFeatures = [
  "Essential AI emotional support",
  "Individually tailored Reflection Guide",
  "Text-based conversations",
  "Basic emotional awareness building",
  "7-day conversation history",
  "24/7 availability"
];

const premiumFeatures = [
  "Everything in Text Companion",
  "AI Voice Connect Messaging",
  "Advanced emotional awareness building",
  "Personalized growth insights",
  "Empathic Listening and pattern analysis",
  "Extended conversation history",
  "Tailored emotional wellness tips",
  "FeelHeard Adaptive Emotional Intelligence"
];

const basicLimitations = [
  "No voice conversations",
  "Limited conversation history"
];

/**
 * Checkout form component that collects payment details
 */
function CheckoutForm({ clientSecret, onSuccess, priceLabel, tier, description }: { 
  clientSecret: string; 
  onSuccess: () => void;
  priceLabel: string;
  tier: 'basic' | 'premium';
  description: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [name, setName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('US');
  
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }
    
    const cardElement = elements.getElement(CardElement);
    if (!cardElement) {
      return;
    }
    
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    
    if (!addressLine1.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      setError('Please complete your billing address');
      return;
    }
    
    setProcessing(true);
    
    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name,
            address: {
              line1: addressLine1,
              line2: addressLine2 || undefined,
              city,
              state,
              postal_code: zip,
              country
            }
          },
        },
      });
      
      if (error) {
        setError(error.message || 'An error occurred with your payment.');
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        onSuccess();
      }
    } catch (err) {
      setError('An unexpected error occurred.');
      console.error('Payment error:', err);
    } finally {
      setProcessing(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {/* Selected Plan Summary */}
        <div className="border-b pb-4 mb-2">
          <h3 className="text-2xl font-bold text-gray-800 mb-1 font-['Poppins']">
            {tier === 'premium' ? 'Voice Connect' : 'Text Companion'}
          </h3>
          <p className="text-gray-600 mb-2">{description}</p>
          <div className="flex flex-col">
            <span className="text-xl font-bold text-gray-900">{priceLabel}</span>
            {priceLabel.includes('/year') && (
              <p className="text-sm text-[#B390C9] mt-1 flex items-center">
                <IconInfoCircle size={14} className="mr-1 flex-shrink-0" />
                You'll be charged the full annual amount today
              </p>
            )}
          </div>
        </div>
        
        <Text fw={500} className="text-xl font-bold text-gray-800">Complete your subscription</Text>
        
        {/* Billing Information */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h4 className="text-lg font-semibold mb-4">Billing Information</h4>
          
          {/* Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#C2A4D6] focus:border-[#C2A4D6]"
              placeholder="John Doe"
              required
            />
          </div>
          
          {/* Address */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#C2A4D6] focus:border-[#C2A4D6]"
              placeholder="123 Main St"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2 (Optional)</label>
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#C2A4D6] focus:border-[#C2A4D6]"
              placeholder="Apt 4B"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#C2A4D6] focus:border-[#C2A4D6]"
                placeholder="San Francisco"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#C2A4D6] focus:border-[#C2A4D6]"
                placeholder="CA"
                required
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ZIP Code</label>
              <input
                type="text"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#C2A4D6] focus:border-[#C2A4D6]"
                placeholder="94103"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#C2A4D6] focus:border-[#C2A4D6]"
                required
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
                <option value="GB">United Kingdom</option>
                <option value="AU">Australia</option>
                <option value="NZ">New Zealand</option>
              </select>
            </div>
          </div>
        </div>
        
        {/* Payment Information */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <h4 className="text-lg font-semibold mb-4">Payment Information</h4>
          <CardElement 
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
        
        {error && (
          <Alert color="red" title="Payment Error">
            {error}
          </Alert>
        )}
        
        <Group justify="space-between" align="center">
          <div>
            <Text fw={500} className="text-lg">Total: {priceLabel}</Text>
            {priceLabel.includes('/year') && (
              <Text size="xs" c="dimmed">One-time annual payment</Text>
            )}
          </div>
          <Button
            type="submit"
            disabled={!stripe || processing}
            loading={processing}
            leftSection={<IconCreditCard size={16} />}
            className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white py-2 px-6 rounded-full transition-colors"
          >
            Submit Payment
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

/**
 * Subscription management component with beautiful styling matching Pricing.tsx
 */
export function SubscriptionManager() {
  const medplum = useMedplum();
  const [isAnnual, setIsAnnual] = useState(false);
  // Default to null (no selection) instead of premium
  const [selectedTier, setSelectedTier] = useState<'basic' | 'premium' | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [cancelComplete, setCancelComplete] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  
  // Get complete subscription data including profile - SINGLE hook call
  const { 
    subscriptionStatus, 
    subscriptionTier, 
    billingCycle,
    isTrialActive,
    trialDaysRemaining,
    isPremium,
    isBasic,
    isFree,
    conversationCount,
    conversationLimit,
    // conversationsRemaining is no longer available
    createSubscription,
    cancelSubscription,
    getPriceId,
    profile // Make sure to get profile first before using it in any hooks
  } = useSubscriptionFeatures();

  // Define price IDs for annual plans
  const annualPriceIds = [
    'price_1RI3nMIfLgrjtRiqVPTBy2T2', // Premium Annual Test
    'price_1RI3nMIfLgrjtRiqPdc1FNWa', // Basic Annual Test
    'price_1RI4BPIfLgrjtRiq1faFDCth', // Premium Annual Prod
    'price_1RI4BPIfLgrjtRiqzs5PZvEc'  // Basic Annual Prod
  ];
  
  // Define price IDs for monthly plans
  const monthlyPriceIds = [
    'price_1RI3nMIfLgrjtRiq9tXej1QM', // Basic Monthly
    'price_1RI3nMIfLgrjtRiqDQm2jQPj'  // Premium Monthly
  ];

  // Function to determine if a price ID is for an annual plan
  const isAnnualPriceId = useCallback((priceId: string | undefined) => {
    return !!priceId && annualPriceIds.includes(priceId);
  }, [annualPriceIds]);
  
  // Function to check if the current subscription is annual based on its price ID
  const checkIfCurrentPlanIsAnnual = useCallback(() => {
    // First check if we have a subscription plan (coming from create-subscription bot)
    const planExtension = profile?.extension?.find(
      e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-plan'
    );
    const planId = planExtension?.valueString;
    
    console.log('Checking plan type for ID:', planId);
    
    // If we have a plan ID, check if it's an annual plan based on our list
    if (planId) {
      // Directly check if the plan ID is in our annual IDs list
      if (annualPriceIds.includes(planId)) {
        console.log('Found annual plan ID match');
        return true;
      }
      
      // Check if it's explicitly in our monthly list
      if (monthlyPriceIds.includes(planId)) {
        console.log('Found monthly plan ID match');
        return false;
      }
      
      // Log unknown plan ID
      console.log('Unknown plan ID, defaulting to monthly:', planId);
      return false;
    } 
    
    // If we don't have a plan ID, fall back to the billing cycle
    if (billingCycle) {
      console.log('No plan ID, using billing cycle:', billingCycle);
      return billingCycle === 'annual';
    }
    
    console.log('No plan ID or billing cycle found, defaulting to monthly');
    // If all else fails, default to false (monthly)
    return false;
  }, [annualPriceIds, monthlyPriceIds, billingCycle, profile?.extension]);
  
  // Set initial selections based on current subscription, but only on first load
  useEffect(() => {
    // Refresh profile to ensure we have the latest subscription data
    const refreshProfile = async () => {
      try {
        await medplum.getProfile();
        console.log('Profile refreshed on subscription page load');
      } catch (error) {
        console.error('Failed to refresh profile:', error);
      }
    };

    // Refresh profile first
    refreshProfile().then(() => {
      // Only set these values on initial load - add firstLoad ref
      const isFirstLoad = true;
      
      console.log('[SubscriptionManager] Setting initial tier selection:', {
        subscriptionTier,
        isFree: subscriptionTier === 'free',
        isEmpty: !subscriptionTier,
        isPremium: subscriptionTier === 'premium',
        isBasic: subscriptionTier === 'basic'
      });
        
      if (isFirstLoad && subscriptionTier && subscriptionTier !== 'free') {
        console.log('[SubscriptionManager] User has an existing non-free subscription, selecting their current tier');
        setSelectedTier(subscriptionTier === 'premium' ? 'premium' : 'basic');
      } else if (isFirstLoad && (!subscriptionTier || subscriptionTier === 'free')) {
        console.log('[SubscriptionManager] User is on free tier or has no subscription, no default tier selected');
        // REMOVED: No longer defaulting to premium for free/new users
        // setSelectedTier('premium'); 
      }
      
      if (isFirstLoad) {
        // Determine if the current plan is annual
        const isCurrentPlanAnnual = checkIfCurrentPlanIsAnnual();
        console.log('[SubscriptionManager] Plan billing cycle check:', { 
          isCurrentPlanAnnual,
          billingCycle,
          planExtension: profile?.extension?.find(
            e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-plan'
          )?.valueString,
          allExtensions: profile?.extension?.map(e => ({ url: e.url, value: e.valueString || e.valueBoolean || e.valueDateTime }))
        });
        
        // For new users and testing, force monthly billing as default
        if (subscriptionTier === 'free' || !subscriptionTier) {
          console.log('[SubscriptionManager] Free tier user, defaulting to monthly billing');
          setIsAnnual(false);
        } else {
          setIsAnnual(isCurrentPlanAnnual);
        }
      }
      
      // Log subscription details for debugging
      console.log('Subscription details:', {
        subscriptionTier,
        billingCycle,
        planExtension: profile?.extension?.find(
          e => e.url === 'https://progressnotes.app/fhir/StructureDefinition/subscription-plan'
        )?.valueString,
        isCurrentPlanAnnual: checkIfCurrentPlanIsAnnual(),
        selectedTier,
        annualPriceIds: annualPriceIds || [],
        monthlyPriceIds: monthlyPriceIds || [],
        isAnnual, // current UI state
        isSubscriptionActive: subscriptionStatus === 'active',
        isTrialActive
      });
    });
    
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount
  
  // Debug effect for clientSecret and Stripe initialization
  useEffect(() => {
    console.log('Subscription component state:', {
      hasClientSecret: !!clientSecret,
      showPaymentForm,
      stripePromiseReady: !!stripePromise,
      loadingState: loading
    });
    
    if (clientSecret) {
      console.log('Client secret length:', clientSecret.length);
      console.log('Client secret format valid:', clientSecret.startsWith('pi_') || clientSecret.startsWith('seti_'));
    }
  }, [clientSecret, showPaymentForm, loading]);
  
  // Add useEffect to log state changes for debugging
  useEffect(() => {
    console.log('Plan selection changed:', { 
      selectedTier, 
      isAnnual, 
      showPaymentForm, 
      subscriptionTier 
    });
  }, [selectedTier, isAnnual, showPaymentForm, subscriptionTier]);

  // Get price label based on selected options
  const getPriceLabel = useCallback(() => {
    // Provide fallback if selectedTier is null when calculating price initially
    const tierForPrice = selectedTier || 'premium'; // Default to premium for price calculation if null
    const price = isAnnual 
      ? annualPrice[tierForPrice] 
      : monthlyPrice[tierForPrice];
      
    return isAnnual
      ? `$${(price * 12).toFixed(2)}/year ($${price}/mo)`
      : `$${price}/month`;
  }, [selectedTier, isAnnual]);
  
  // Calculate savings for annual billing
  const savedAmount = {
    basic: ((monthlyPrice.basic * 12) - (annualPrice.basic * 12)).toFixed(2),
    premium: ((monthlyPrice.premium * 12) - (annualPrice.premium * 12)).toFixed(2)
  };
  
  // Handle subscription creation or upgrade
  const handleSubscribe = async () => {
    if (!selectedTier) return;
    
    setError(null);
    setIsUpdating(true);
    
    try {
      console.log('handleSubscribe called', { selectedTier, isAnnual });
      console.log('Starting subscription process in UI...');
      console.log('Using Stripe configuration:', { 
        stripeKey, 
        selectedTier, 
        isAnnual,
        environment: import.meta.env.VITE_STRIPE_MODE
      });
      
      // Refresh profile before subscription action to ensure we have the latest data
      try {
        await medplum.getProfile();
        console.log('Profile refreshed before subscription action');
      } catch (refreshError) {
        console.error('Failed to refresh profile before subscription action:', refreshError);
        // Continue with operation even if refresh fails
      }
      
      setLoading(true);
      setError(null);
      
      const result = await createSubscription(selectedTier, isAnnual);
      console.log('Received result from createSubscription:', result);
      
      if (result.clientSecret) {
        console.log('Client secret received, showing payment form...');
        // Store the client secret and payment form state locally first
        const secret = result.clientSecret;
        
        // Set these states one after another (not batched)
        setClientSecret(secret);
        setShowPaymentForm(true);
        
        // Store for later reference in the finally block
        window._latestClientSecret = secret;
      } else if (result.status === 'upgraded') {
        console.log('Subscription upgraded without payment needed');
        // Subscription was updated without requiring payment
        setPaymentComplete(true);
      } else {
        console.log('No client secret or upgrade status received:', result);
        throw new Error(`Unexpected subscription status: ${result.status}`);
      }
    } catch (err) {
      console.error('Subscription error:', err);
      setError((err as Error).message || 'An error occurred during subscription.');
    } finally {
      try {
        // Attempt to get updated profile
        await medplum.getProfile(); 
        console.log('Profile refreshed after subscribe attempt');
      } catch (refreshError) {
        console.error('Failed to refresh profile after subscribe:', refreshError);
        // Don't block UI updates if refresh fails, maybe log or notify
      }
      
      // Always stop the main 'isUpdating' loader regardless of payment state
      // The CheckoutForm will handle its own loading state if needed
      setLoading(false);
      
      // Check if we have a valid client secret (either in state or temp variable)
      const hasSecret = !!(window._latestClientSecret || clientSecret);
      console.log('Payment check:', { 
        hasSecret, 
        showPaymentForm,
        clientSecret: !!clientSecret,
        windowSecret: !!window._latestClientSecret 
      });
      
      // Force isUpdating to false to ensure we don't get stuck with the loader
      // The payment form will be managed separately
      setIsUpdating(false);
    }
  };
  
  // Handle subscription cancellation
  const handleCancel = async () => {
    setError(null);
    setIsUpdating(true);
    
    try {
      console.log('handleCancel called');
      await cancelSubscription();
      // Get updated profile data after successful cancellation
      await medplum.getProfile();
      console.log('Profile refreshed after cancellation');
      
      showNotification({
        title: 'Subscription Canceled',
        message: 'Your subscription has been canceled successfully. Please refresh the page to see the changes.',
        color: 'green',
        icon: <IconCheck />
      });
      
      // Set cancelComplete to true to show refresh UI
      setCancelComplete(true);
    } catch (err) {
      console.error('Cancellation error:', err);
      setError((err as Error).message || 'An error occurred during cancellation.');
    } finally {
      setIsUpdating(false);
    }
  };
  
  // Handle payment success
  const handlePaymentSuccess = async () => {
    console.log('handlePaymentSuccess called');
    setIsUpdating(true);
    setShowPaymentForm(false);
    setClientSecret(null);
    // Clear temporary storage
    if (window._latestClientSecret) {
      delete window._latestClientSecret;
    }
    setPaymentComplete(true);
    
    // Show success message
    const tierName = selectedTier === 'basic' ? 'Text Companion' : 'Voice Connect';
    showNotification({
      title: 'Subscription Updated Successfully',
      message: `Your subscription has been updated successfully. You now have access to all ${tierName} features.`,
      color: 'green',
      icon: <IconCheck />
    });

    // Track subscription conversion event for Facebook Pixel
    // This is important for conversion tracking
    const subscriptionValue = isAnnual 
      ? (selectedTier === 'basic' ? annualPrice.basic * 12 : annualPrice.premium * 12)
      : (selectedTier === 'basic' ? monthlyPrice.basic : monthlyPrice.premium);
    
    trackSubscribe({
      value: subscriptionValue,
      currency: 'USD',
      predicted_ltv: isAnnual ? subscriptionValue : subscriptionValue * 12 // Estimate yearly value
    });
    
    console.log('Facebook Pixel: Subscription event tracked', {
      tier: selectedTier,
      isAnnual,
      value: subscriptionValue
    });

    // Get updated profile to reflect the new subscription status from payment
    try {
      await medplum.getProfile();
      console.log('Profile refreshed after payment success');
    } catch (err) {
      console.error('Failed to refresh profile after payment:', err);
      // Optionally show a notification to the user that the UI might be slightly delayed
    } finally {
      setIsUpdating(false);
    }
  };
  
  return (
    <Box pos="relative">
      <LoadingOverlay visible={loading} />
      
      {paymentComplete && (
        <div className="mb-8 bg-[#A9D0A1]/20 text-[#2C742F] p-4 rounded-lg flex items-start">
          <IconCheck size={20} className="mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Subscription Updated Successfully</p>
            <p>
              Your subscription has been updated successfully. You now have access to all 
              {selectedTier === 'premium' ? ' Voice Connect' : ' Text Companion'} features. 
              Please refresh screen to show subscription changes. It may take up to 30 seconds for changes to be reflected in the system.
            </p>
            <button 
              onClick={() => {
                // Show a loading overlay for 5 seconds before refreshing
                setIsUpdating(true);
                setTimeout(() => {
                  window.location.reload();
                }, 5000);
              }}
              className="mt-3 bg-[#2C742F] hover:bg-[#1F5320] text-white py-2 px-4 rounded-full text-sm transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )}
      
      {cancelComplete && (
        <div className="mb-8 bg-[#A9D0A1]/20 text-[#2C742F] p-4 rounded-lg flex items-start">
          <IconCheck size={20} className="mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Subscription Canceled Successfully</p>
            <p>
              Your subscription has been canceled successfully. 
              Please refresh screen to show subscription changes. It may take up to 30 seconds for changes to be reflected in the system.
            </p>
            <button 
              onClick={() => {
                // Show a loading overlay for 5 seconds before refreshing
                setIsUpdating(true);
                setTimeout(() => {
                  window.location.reload();
                }, 5000);
              }}
              className="mt-3 bg-[#2C742F] hover:bg-[#1F5320] text-white py-2 px-4 rounded-full text-sm transition-colors"
            >
              Refresh Page
            </button>
          </div>
        </div>
      )}
      
      {error && (
        <div className="mb-8 bg-red-100 text-red-700 p-4 rounded-lg flex items-start">
          <IconAlertCircle size={20} className="mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Error</p>
            <p>{error}</p>
          </div>
        </div>
      )}
      
      {isTrialActive && !paymentComplete && (
        <div className="mb-8 bg-[#C2A4D6]/20 text-[#9068A8] p-4 rounded-lg flex items-start">
          <IconInfoCircle size={20} className="mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Trial Period</p>
            <p>
              You are currently on a free trial with {trialDaysRemaining} {trialDaysRemaining === 1 ? 'day' : 'days'} remaining.
              To continue your journey with us, please select a subscription plan below.
            </p>
          </div>
        </div>
      )}
      
      {/* Current subscription card */}
      {((isBasic || isPremium) && !isTrialActive) && !showPaymentForm && (
        <div className={`mb-8 relative ${paymentComplete || cancelComplete ? 'opacity-50' : ''}`}>
          {(paymentComplete || cancelComplete) && (
            <div className="absolute inset-0 bg-gray-200 bg-opacity-50 flex items-center justify-center rounded-xl z-10">
              <button 
                onClick={() => window.location.reload()} 
                className="bg-[#C2A4D6] hover:bg-[#B390C9] text-white py-2 px-6 rounded-full text-lg font-medium transition-colors"
              >
                Refresh to See Updated Subscription
              </button>
            </div>
          )}
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Current Subscription</h3>
          <div className="bg-white rounded-2xl shadow-md p-6 border border-gray-200 overflow-hidden">
            <div className="flex justify-between items-center flex-wrap">
              <div>
                <p className="text-xl font-bold text-gray-800">
                  {subscriptionTier === 'premium' ? 'Voice Connect' : 'Text Companion'}
                </p>
                <p className="text-gray-600">
                  {checkIfCurrentPlanIsAnnual() ? 'Billed annually' : 'Billed monthly'}
                </p>
                {checkIfCurrentPlanIsAnnual() && (
                  <p className="text-xs text-[#F4B6A1] mt-1">
                    Annual billing saves you {subscriptionTier === 'premium' ? savedAmount.premium : savedAmount.basic} per year
                  </p>
                )}
              </div>
              
              <button 
                onClick={handleCancel}
                className="bg-white text-red-500 border border-red-500 py-2 px-4 rounded-full text-sm font-medium hover:bg-red-50 transition-colors"
                disabled={paymentComplete || cancelComplete}
              >
                Cancel Subscription
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Billing toggle and Plan selection UI */}
      <div className={(paymentComplete || cancelComplete) ? 'opacity-50 pointer-events-none' : ''}>
        {/* Billing toggle */}
        <div className="max-w-3xl mx-auto text-center mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">
            {isBasic || isPremium ? 'Change Subscription' : 'Choose a Plan'}
          </h3>
          
          <div className="flex items-center justify-center mb-4">
            <span className={`mr-4 ${!isAnnual ? 'font-semibold text-[#C2A4D6]' : 'text-gray-600'}`}>
              Monthly
            </span>
            <button 
              className={`relative w-16 h-8 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#C2A4D6] ${
                isAnnual ? 'bg-[#C2A4D6]' : 'bg-gray-300'
              }`}
              onClick={(e) => {
                e.preventDefault(); // Prevent default
                e.stopPropagation(); // Stop propagation
                
                // Toggle the annual state
                const newIsAnnual = !isAnnual;
                setIsAnnual(newIsAnnual);
                console.log('Toggle billing cycle:', { current: isAnnual, new: newIsAnnual });
                
                // If in payment form, automatically restart subscription process with new billing cycle
                if (showPaymentForm) {
                  setShowPaymentForm(false);
                  setClientSecret(null);
                  
                  // Wait a tick for state to update before re-triggering subscription
                  setTimeout(() => {
                    handleSubscribe();
                  }, 100); // Longer timeout for more reliable state update
                }
              }}
            >
              <span 
                className={`absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition-transform transform ${
                  isAnnual ? 'translate-x-8' : ''
                }`}
              />
            </button>
            <span className={`ml-4 ${isAnnual ? 'font-semibold text-[#C2A4D6]' : 'text-gray-600'}`}>
              Annual <span className="text-[#F4B6A1] text-sm font-medium">(Save 20%)</span>
            </span>
          </div>
          
          {showPaymentForm && (
            <div className="mt-2 text-sm text-[#C2A4D6]">
              <span className="inline-flex items-center">
                <IconInfoCircle size={14} className="mr-1" />
                You can change billing cycle with one click
              </span>
            </div>
          )}
        </div>
        
        <div className="md:flex md:flex-col md:items-center">
          {/* Subscription plans */}
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-8 w-full">
            {/* Basic Plan */}
            <div 
              className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-transform 
                ${showPaymentForm ? 'cursor-pointer' : 'transform hover:scale-105 cursor-pointer'} 
                ${selectedTier === 'basic' ? 'border-2 border-[#B3D9E6]' : 'border border-gray-200'}
                ${showPaymentForm && selectedTier !== 'basic' ? 'opacity-50' : ''}
                relative`}
              onClick={(e) => {
                e.preventDefault();
                console.log('Basic plan card clicked, current tier:', selectedTier);
                
                if (selectedTier !== 'basic') {
                  console.log('Changing tier to basic');
                  setSelectedTier('basic');
                  
                  // If in payment form, restart subscription process with new plan
                  if (showPaymentForm) {
                    setShowPaymentForm(false);
                    setClientSecret(null);
                    
                    // Wait a tick for state to update before re-triggering subscription
                    setTimeout(() => {
                      handleSubscribe();
                    }, 100); // Increased timeout for reliability
                  }
                }
              }}
            >
              {/* Overlay for non-selected plan when in payment form */}
              {showPaymentForm && selectedTier !== 'basic' && (
                <div className="absolute inset-0 bg-white bg-opacity-30 z-10 flex items-center justify-center">
                  <div className="bg-white p-3 rounded-lg shadow-md">
                    <button 
                      className="bg-[#B3D9E6] text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-[#9FCFDF] transition-colors"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event bubbling
                        console.log('[SubscriptionManager] Switch to Basic Plan button clicked');
                        
                        // First reset all loading and payment states to clean state
                        setLoading(false);
                        setIsUpdating(false);
                        setShowPaymentForm(false);
                        setClientSecret(null);
                        
                        // Clear any stored client secret
                        if (window._latestClientSecret) {
                          delete window._latestClientSecret;
                        }
                        
                        // Change tier and defer subscribe until next render cycle
                        setSelectedTier('basic');
                        
                        // Use a longer timeout to ensure state is fully updated
                        setTimeout(() => {
                          console.log('[SubscriptionManager] Calling handleSubscribe after tier change');
                          handleSubscribe();
                        }, 250);
                      }}
                    >
                      Switch to Text Companion
                    </button>
                  </div>
                </div>
              )}
              <div className={`bg-[#B3D9E6]/20 p-6`}>
                <h3 className="text-2xl font-bold text-gray-800 mb-1 font-['Poppins']">Text Companion</h3>
                <p className="text-gray-600 mb-4">Perfect for occasional check-ins</p>
                <div className="flex items-baseline">
                  {isAnnual ? (
                    <>
                      <span className="text-3xl font-bold text-gray-900">
                        ${(annualPrice.basic * 12).toFixed(2)}
                      </span>
                      <span className="text-gray-600 ml-1">/year</span>
                      <span className="text-gray-600 ml-2 text-sm">
                        (${annualPrice.basic}/mo)
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-gray-900">
                        ${monthlyPrice.basic}
                      </span>
                      <span className="text-gray-600 ml-1">/month</span>
                    </>
                  )}
                </div>
                {isAnnual && (
                  <p className="text-[#F4B6A1] text-sm mt-2">
                    Save ${savedAmount.basic} per year
                  </p>
                )}
              </div>
              
              <div className="p-6 space-y-4">
                <ul className="space-y-3">
                  {basicFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {!showPaymentForm && (
                  <div className="pt-4">
                    <button 
                      className={`w-full py-3 px-6 rounded-full text-lg font-medium transition-colors
                        ${selectedTier === 'basic' 
                          ? 'bg-[#B3D9E6] text-white'
                          : 'bg-white text-[#B3D9E6] border-2 border-[#B3D9E6] hover:bg-[#B3D9E6]/10'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event bubbling
                        setSelectedTier('basic');
                      }}
                    >
                      {selectedTier === 'basic' ? 'Selected' : 'Select'}
                    </button>
                  </div>
                )}
                
                {showPaymentForm && selectedTier !== 'basic' && (
                  <div className="pt-4">
                    <button 
                      className="w-full py-3 px-6 rounded-full text-lg font-medium transition-colors
                        bg-white text-[#B3D9E6] border-2 border-[#B3D9E6] hover:bg-[#B3D9E6]/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTier('basic');
                        setShowPaymentForm(false);
                        setClientSecret(null);
                        
                        // Wait a tick for state to update before re-triggering subscription
                        setTimeout(() => {
                          handleSubscribe();
                        }, 10);
                      }}
                    >
                      Switch to Text Companion
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Premium Plan */}
            <div 
              className={`bg-white rounded-2xl shadow-lg overflow-hidden transition-transform 
                ${showPaymentForm ? 'cursor-pointer' : 'transform hover:scale-105 cursor-pointer'} 
                ${selectedTier === 'premium' ? 'border-2 border-[#C2A4D6]' : 'border border-gray-200'}
                ${showPaymentForm && selectedTier !== 'premium' ? 'opacity-50' : ''}
                relative`}
              onClick={(e) => {
                e.preventDefault();
                console.log('Premium plan card clicked, current tier:', selectedTier);
                
                if (selectedTier !== 'premium') {
                  console.log('Changing tier to premium');
                  setSelectedTier('premium');
                  
                  // If in payment form, restart subscription process with new plan
                  if (showPaymentForm) {
                    setShowPaymentForm(false);
                    setClientSecret(null);
                    
                    // Wait a tick for state to update before re-triggering subscription
                    setTimeout(() => {
                      handleSubscribe();
                    }, 100); // Increased timeout for reliability
                  }
                }
              }}
            >
              {/* Overlay for non-selected plan when in payment form */}
              {showPaymentForm && selectedTier !== 'premium' && (
                <div className="absolute inset-0 bg-white bg-opacity-30 z-10 flex items-center justify-center">
                  <div className="bg-white p-3 rounded-lg shadow-md">
                    <button 
                      className="bg-[#C2A4D6] text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-[#B390C9] transition-colors"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event bubbling
                        console.log('[SubscriptionManager] Switch to Premium Plan button clicked');
                        
                        // First reset all loading and payment states to clean state
                        setLoading(false);
                        setIsUpdating(false);
                        setShowPaymentForm(false);
                        setClientSecret(null);
                        
                        // Clear any stored client secret
                        if (window._latestClientSecret) {
                          delete window._latestClientSecret;
                        }
                        
                        // Change tier and defer subscribe until next render cycle
                        setSelectedTier('premium');
                        
                        // Use a longer timeout to ensure state is fully updated
                        setTimeout(() => {
                          console.log('[SubscriptionManager] Calling handleSubscribe after tier change');
                          handleSubscribe();
                        }, 250);
                      }}
                    >
                      Switch to Voice Connect
                    </button>
                  </div>
                </div>
              )}
              
              <div className="absolute top-0 right-0 z-20">
                <div className="bg-[#F4B6A1] text-white px-4 py-1 rounded-bl-lg font-medium text-sm">
                  Most Popular
                </div>
              </div>
              
              <div className={`bg-[#C2A4D6]/20 p-6`}>
                <h3 className="text-2xl font-bold text-gray-800 mb-1 font-['Poppins']">Voice Connect</h3>
                <p className="text-gray-600 mb-4">For comprehensive emotional support</p>
                <div className="flex items-baseline">
                  {isAnnual ? (
                    <>
                      <span className="text-3xl font-bold text-gray-900">
                        ${(annualPrice.premium * 12).toFixed(2)}
                      </span>
                      <span className="text-gray-600 ml-1">/year</span>
                      <span className="text-gray-600 ml-2 text-sm">
                        (${annualPrice.premium}/mo)
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-gray-900">
                        ${monthlyPrice.premium}
                      </span>
                      <span className="text-gray-600 ml-1">/month</span>
                    </>
                  )}
                </div>
                {isAnnual && (
                  <p className="text-[#F4B6A1] text-sm mt-2">
                    Save ${savedAmount.premium} per year
                  </p>
                )}
              </div>
              
              <div className="p-6 space-y-4">
                <ul className="space-y-3">
                  {premiumFeatures.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="w-5 h-5 text-[#A9D0A1] mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {!showPaymentForm && (
                  <div className="pt-4">
                    <button 
                      className={`w-full py-3 px-6 rounded-full text-lg font-medium transition-colors
                        ${selectedTier === 'premium' 
                          ? 'bg-[#C2A4D6] text-white'
                          : 'bg-white text-[#C2A4D6] border-2 border-[#C2A4D6] hover:bg-[#C2A4D6]/10'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent event bubbling
                        setSelectedTier('premium');
                      }}
                    >
                      {selectedTier === 'premium' ? 'Selected' : 'Select'}
                    </button>
                  </div>
                )}
                
                {showPaymentForm && selectedTier !== 'premium' && (
                  <div className="pt-4">
                    <button 
                      className="w-full py-3 px-6 rounded-full text-lg font-medium transition-colors
                        bg-white text-[#C2A4D6] border-2 border-[#C2A4D6] hover:bg-[#C2A4D6]/10"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTier('premium');
                        setShowPaymentForm(false);
                        setClientSecret(null);
                        
                        // Wait a tick for state to update before re-triggering subscription
                        setTimeout(() => {
                          handleSubscribe();
                        }, 10);
                      }}
                    >
                      Switch to Voice Connect
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Payment Form - Now positioned below the plans */}
          {clientSecret && showPaymentForm && (
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden p-8 mb-8 max-w-2xl w-full mx-auto">
              <Elements stripe={stripePromise} options={{ 
                clientSecret,
                appearance: { theme: 'stripe' }
              }}>
                <CheckoutForm 
                  clientSecret={clientSecret}
                  onSuccess={handlePaymentSuccess}
                  priceLabel={getPriceLabel()}
                  tier={selectedTier || 'premium'}
                  description={selectedTier === 'premium' 
                    ? "Voice-enabled emotional support" 
                    : "Text-based emotional support"}
                />
              </Elements>
            </div>
          )}
          
          {/* Subscribe button */}
          {!showPaymentForm && (
            <div className="text-center mx-auto max-w-3xl">
              <button
                onClick={handleSubscribe}
                disabled={loading || !selectedTier}
                className={`w-full bg-[#C2A4D6] hover:bg-[#B390C9] text-white py-3 px-6 rounded-full text-lg font-medium transition-colors ${
                  (loading || !selectedTier) ? 'opacity-70 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Processing...' : 
                 isTrialActive ? 'Choose Plan & Continue After Trial' :
                 subscriptionStatus === 'active' ? 'Update Subscription' : 
                 'Subscribe Now'}
              </button>
              
              <p className="text-sm text-gray-500 mt-4">
                You can cancel your subscription at any time.
              </p>
            </div>
          )}
        </div>
      </div>
      
      {/* Loading overlay for update actions */}
      <LoadingOverlay visible={isUpdating} overlayProps={{ radius: "sm", blur: 2 }} />
    </Box>
  );
}